import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useMediaQuery } from 'usehooks-ts';
import { useAuth } from '../../lib/auth-context';
import { notificationsAPI } from '../../lib/api';
import { useReviewMode, REVIEW_MODE_SUMMARY } from '../../lib/review-mode';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import { cn } from '../../lib/utils';
import { sectionItemsWithTeams } from '../../components/layout/sidebar-items';
import '../../styles/admin-theme.css';

/* ── Brand Logo ─────────────────────────────────────────────────────── */
function NeuralIcon({ className }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="9" fill="currentColor" />
      <path d="M10 22V10h2.4l5.6 8.4V10H20v12h-2.4L12 13.6V22H10Z" fill="#FFFFFF" />
    </svg>
  );
}

/* ── Safe localStorage ──────────────────────────────────────────────── */
const store = {
  get(key, fallback) {
    try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch { return fallback; }
  },
  set(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  },
};

/* ── Theme Hook (syncs with PublicLayout via localStorage 'theme') ──── */
function readTheme() {
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
  } catch {}
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

function useAdminTheme() {
  const [darkMode, setDarkMode] = useState(readTheme);

  // Scope the admin theme to <html> while the admin is mounted so Radix
  // portals pick it up; remove it again when leaving for the public site.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('nn-admin');
    return () => root.classList.remove('nn-admin');
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', darkMode);
    root.style.colorScheme = darkMode ? 'dark' : 'light';
    try { localStorage.setItem('theme', darkMode ? 'dark' : 'light'); } catch {}
  }, [darkMode]);

  // Theme changes from AppearanceSetting or other tabs
  useEffect(() => {
    const onStorage = () => setDarkMode(readTheme());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback((dark) => {
    const root = document.documentElement;
    root.classList.add('nn-theme-switching');
    setDarkMode(dark);
    window.setTimeout(() => root.classList.remove('nn-theme-switching'), 300);
  }, []);

  return { darkMode, setTheme };
}

/* ── Breadcrumbs Hook ───────────────────────────────────────────────── */
function useBreadcrumbs() {
  const location = useLocation();
  return useMemo(() => {
    const parts = location.pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
    const crumbs = [{ label: 'Admin', path: '/admin' }];
    let accum = '/admin';
    for (const part of parts) {
      accum += `/${part}`;
      crumbs.push({ label: part.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), path: accum });
    }
    return crumbs;
  }, [location.pathname]);
}

/* ── Nav matching ───────────────────────────────────────────────────── */
function matches(item, pathname) {
  if (item.exact) return pathname === item.href;
  return pathname.startsWith(item.href) && item.href !== '/admin';
}

function usePageTitle() {
  const location = useLocation();
  for (const section of sectionItemsWithTeams) {
    for (const item of section.items) {
      if (matches(item, location.pathname)) return item.title;
    }
  }
  return 'Overview';
}

/* ── Light / Dark segmented switch ──────────────────────────────────── */
function ThemeSwitch({ darkMode, setTheme, compact = false, className }) {
  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setTheme(!darkMode)}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-[var(--adm-side-muted)] hover:bg-[var(--adm-side-hover)] hover:text-[var(--adm-side-text)] transition-colors"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <Icon icon={darkMode ? 'solar:sun-2-bold-duotone' : 'solar:moon-bold-duotone'} width={22} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={12} className="text-xs">{darkMode ? 'Light mode' : 'Dark mode'}</TooltipContent>
      </Tooltip>
    );
  }
  const opt = (dark, icon, label) => (
    <button
      type="button"
      role="radio"
      aria-checked={darkMode === dark}
      onClick={() => setTheme(dark)}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition-all',
        darkMode === dark
          ? 'bg-[var(--adm-surface)] text-[var(--adm-text)] shadow-sm ring-1 ring-[var(--adm-border)]'
          : 'text-[var(--adm-muted)] hover:text-[var(--adm-text)]',
      )}
      data-testid={`admin-theme-${dark ? 'dark' : 'light'}`}
    >
      <Icon icon={icon} width={16} className={darkMode === dark ? (dark ? 'text-sky-300' : 'text-amber-500') : ''} />
      {label}
    </button>
  );
  return (
    <div role="radiogroup" aria-label="Colour theme" className={cn('flex rounded-xl bg-[var(--adm-surface-2)] p-1 ring-1 ring-inset ring-[var(--adm-border)]', className)}>
      {opt(false, 'solar:sun-2-bold-duotone', 'Light')}
      {opt(true, 'solar:moon-bold-duotone', 'Dark')}
    </div>
  );
}

/* ── Review-mode pill (always visible in header) ────────────────────── */
function ReviewModePill({ canEdit }) {
  const { on, saving, setMode } = useReviewMode();
  if (on === null) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex h-9 items-center gap-2 rounded-full px-3 text-[12px] font-semibold transition-colors ring-1 ring-inset',
            on
              ? 'bg-[var(--adm-brand)] text-white ring-transparent hover:bg-[var(--adm-brand-hover)]'
              : 'bg-[var(--adm-surface-2)] text-[var(--adm-muted)] ring-[var(--adm-border)] hover:text-[var(--adm-text)]',
          )}
          data-testid="review-mode-pill"
          aria-label={`App review mode is ${on ? 'on' : 'off'}`}
        >
          <span className="relative flex h-2 w-2">
            {on && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80" />}
            <span className={cn('relative inline-flex h-2 w-2 rounded-full', on ? 'bg-white' : 'bg-[var(--adm-subtle)]')} />
          </span>
          <Icon icon="solar:shield-check-bold-duotone" width={16} />
          <span className="hidden sm:inline">Review mode</span>
          <span className={cn('rounded-full px-1.5 py-px text-[10px] uppercase tracking-wider', on ? 'bg-white/20' : 'bg-[var(--adm-hover)]')}>
            {on ? 'On' : 'Off'}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-80 rounded-2xl p-4 border-[var(--adm-border)] bg-[var(--adm-surface)] shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[var(--adm-text)]">App Store &amp; Play Store review mode</p>
            <p className="mt-1 text-xs leading-relaxed text-[var(--adm-muted)]">{REVIEW_MODE_SUMMARY}</p>
          </div>
          <Switch
            checked={on}
            disabled={saving || !canEdit}
            onCheckedChange={setMode}
            aria-label="App review mode"
            data-testid="review-mode-switch"
            className="mt-0.5 data-[state=checked]:bg-[var(--adm-brand)]"
          />
        </div>
        {!canEdit && (
          <p className="mt-3 rounded-lg bg-[var(--adm-surface-2)] p-2 text-[11px] text-[var(--adm-muted)]">
            Only admins can change review mode.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

/* ── AdminLayout ────────────────────────────────────────────────────── */
export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode, setTheme } = useAdminTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => store.get('nn_admin_sidebar_collapsed', false));
  const [navQuery, setNavQuery] = useState('');
  const [closedSections, setClosedSections] = useState(() => store.get('nn_admin_sidebar_closed', null));
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const breadcrumbs = useBreadcrumbs();
  const pageTitle = usePageTitle();

  const isMobile = useMediaQuery('(max-width: 768px)');
  const isCompact = isCollapsed && !isMobile;
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  useEffect(() => { store.set('nn_admin_sidebar_collapsed', isCollapsed); }, [isCollapsed]);

  // Apply a custom accent only if the user picked one that isn't the
  // retired gold default — otherwise the theme's Signal Red applies.
  useEffect(() => {
    const prefs = store.get('nn_appearance_prefs', {});
    const hex = (prefs.accentColor || '').toUpperCase();
    const root = document.documentElement;
    if (/^#[0-9A-F]{6}$/.test(hex) && hex !== '#B89B5E') {
      root.style.setProperty('--nn-accent', hex);
      root.style.setProperty('--adm-brand', hex);
      const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
      root.style.setProperty('--nn-accent-rgb', `${r},${g},${b}`);
    }
    return () => ['--nn-accent', '--adm-brand', '--nn-accent-rgb'].forEach(p => root.style.removeProperty(p));
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.list({ unread_only: true, limit: 5 });
      setUnreadCount(res.data.unread_count || 0);
      setNotifications(res.data.notifications || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const hasPermission = useCallback((item) => {
    if (!item.permission) return true;
    if (item.permission === 'manage_users') return isAdmin;
    if (item.permission === 'super_admin') return user?.role === 'super_admin';
    return true;
  }, [isAdmin, user?.role]);

  // Sections visible to this user, filtered by the sidebar search.
  const sections = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    return sectionItemsWithTeams
      .map(s => ({
        ...s,
        items: s.items.filter(i => hasPermission(i) && (!q || i.title.toLowerCase().includes(q))),
      }))
      .filter(s => s.items.length > 0);
  }, [navQuery, hasPermission]);

  const activeSectionKey = useMemo(() => {
    for (const s of sectionItemsWithTeams) if (s.items.some(i => matches(i, location.pathname) || (i.exact && location.pathname === i.href))) return s.key;
    return 'content';
  }, [location.pathname]);

  // Default: only Content and the section you're in are open.
  const isSectionOpen = (key) => {
    if (navQuery.trim()) return true;
    if (closedSections === null) return key === 'content' || key === activeSectionKey;
    return !closedSections.includes(key) || key === activeSectionKey;
  };

  const toggleSection = (key) => {
    const current = closedSections === null
      ? sectionItemsWithTeams.map(s => s.key).filter(k => !(k === 'content' || k === activeSectionKey))
      : closedSections;
    const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    setClosedSections(next);
    store.set('nn_admin_sidebar_closed', next);
  };

  const isActive = (item) => (item.exact ? location.pathname === item.href : location.pathname.startsWith(item.href));
  const handleLogout = () => { logout(); navigate('/login'); };
  const onToggle = useCallback(() => setIsCollapsed(prev => !prev), []);

  const headerIconBtn = 'flex h-9 w-9 items-center justify-center rounded-xl text-[var(--adm-muted)] hover:bg-[var(--adm-hover)] hover:text-[var(--adm-text)] transition-colors';

  const avatar = (size) => (
    user?.avatar_url ? (
      <img src={user.avatar_url} alt="" className={cn(size, 'rounded-full object-cover')} />
    ) : (
      <div className={cn(size, 'rounded-full flex items-center justify-center text-[11px] font-bold text-white')} style={{ background: 'linear-gradient(135deg, var(--adm-brand), color-mix(in srgb, var(--adm-brand) 65%, black))' }}>
        {user?.full_name?.charAt(0) || 'U'}
      </div>
    )
  );

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-dvh w-full bg-[var(--adm-bg)] text-[var(--adm-text)]">

        {/* ── Mobile overlay ──────────────────────────────────────── */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* ══════════════════════════════════════════════════════════
            SIDEBAR
            ══════════════════════════════════════════════════════════ */}
        <aside
          data-testid="admin-sidebar"
          className={cn(
            'fixed md:relative top-0 left-0 z-40 flex h-full flex-col border-r border-[var(--adm-side-border)] bg-[var(--adm-side-bg)] transition-[width,transform] duration-300 ease-in-out',
            isCompact ? 'w-[76px] items-center' : 'w-[272px]',
            sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full md:translate-x-0',
          )}
        >
          {/* Brand */}
          <div className={cn('flex h-16 w-full flex-none items-center gap-3 px-5', isCompact && 'justify-center px-0')}>
            <Link to="/admin" className="flex items-center gap-2.5 min-w-0" onClick={() => setSidebarOpen(false)}>
              <NeuralIcon className="h-8 w-8 flex-none text-[var(--adm-brand)]" />
              {!isCompact && (
                <span className="flex flex-col leading-none">
                  <span className="text-[14px] font-extrabold tracking-tight text-[var(--adm-text)]">Neural News</span>
                  <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--adm-brand)]">Newsroom</span>
                </span>
              )}
            </Link>
            {!isCompact && (
              <button
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-[var(--adm-side-muted)] hover:bg-[var(--adm-side-hover)] hover:text-[var(--adm-side-text)] transition-colors"
                onClick={isMobile ? () => setSidebarOpen(false) : onToggle}
                aria-label={isMobile ? 'Close menu' : 'Collapse sidebar'}
              >
                <Icon icon={isMobile ? 'solar:close-circle-linear' : 'solar:sidebar-minimalistic-linear'} width={20} />
              </button>
            )}
          </div>

          {/* Quick actions + nav search */}
          {!isCompact ? (
            <div className="w-full flex-none space-y-2 px-4 pb-3">
              <Link
                to="/admin/articles/new"
                onClick={() => setSidebarOpen(false)}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[var(--adm-brand)] text-[13px] font-semibold text-white shadow-sm shadow-[var(--adm-brand-ring)] hover:bg-[var(--adm-brand-hover)] transition-colors"
              >
                <Icon icon="solar:pen-new-square-bold" width={18} /> New article
              </Link>
              <div className="relative">
                <Icon icon="solar:magnifer-linear" width={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--adm-side-muted)]" />
                <input
                  value={navQuery}
                  onChange={e => setNavQuery(e.target.value)}
                  placeholder="Find a page…"
                  aria-label="Filter menu"
                  className="h-9 w-full rounded-xl border border-[var(--adm-side-border)] bg-[var(--adm-surface-2)] pl-9 pr-8 text-[13px] text-[var(--adm-text)] placeholder:text-[var(--adm-side-muted)] outline-none focus:border-[var(--adm-brand-ring)] focus:ring-2 focus:ring-[var(--adm-brand-soft)] transition"
                  data-testid="admin-nav-search"
                />
                {navQuery && (
                  <button onClick={() => setNavQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--adm-side-muted)] hover:text-[var(--adm-side-text)]" aria-label="Clear filter">
                    <Icon icon="solar:close-circle-bold" width={16} />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/admin/articles/new" className="mb-3 flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[var(--adm-brand)] text-white hover:bg-[var(--adm-brand-hover)] transition-colors" aria-label="New article">
                  <Icon icon="solar:pen-new-square-bold" width={20} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={12} className="text-xs">New article</TooltipContent>
            </Tooltip>
          )}

          {/* Nav */}
          <nav className={cn('nn-scroll flex-1 w-full overflow-y-auto overflow-x-hidden pb-4', isCompact ? 'px-3' : 'px-3')}>
            {sections.length === 0 && (
              <p className="px-3 py-6 text-center text-[12px] text-[var(--adm-side-muted)]">No pages match “{navQuery}”.</p>
            )}
            {sections.map((section) => {
              const open = isCompact || isSectionOpen(section.key);
              return (
                <div key={section.key} className="mt-2 first:mt-0">
                  {isCompact ? (
                    <div className="mx-2 my-2 h-px bg-[var(--adm-side-border)]" />
                  ) : (
                    <button
                      onClick={() => toggleSection(section.key)}
                      className="group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[var(--adm-side-muted)] hover:text-[var(--adm-side-text)] transition-colors"
                      aria-expanded={open}
                    >
                      <span className="flex-1 text-left">{section.title}</span>
                      <span className="rounded-full bg-[var(--adm-surface-2)] px-1.5 text-[10px] font-semibold tracking-normal">{section.items.length}</span>
                      <Icon icon="solar:alt-arrow-down-linear" width={14} className={cn('transition-transform', !open && '-rotate-90')} />
                    </button>
                  )}
                  {open && (
                    <div className="space-y-0.5">
                      {section.items.map(item => {
                        const active = isActive(item);
                        const linkEl = (
                          <Link
                            key={item.key}
                            to={item.href}
                            data-testid="admin-nav-item"
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                              'group relative flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] font-medium transition-colors',
                              active
                                ? 'bg-[var(--adm-side-active-bg)] text-[var(--adm-side-active-text)] font-semibold'
                                : 'text-[var(--adm-side-text)] hover:bg-[var(--adm-side-hover)] hover:text-[var(--adm-text)]',
                              isCompact && 'h-11 w-11 justify-center px-0 mx-auto',
                            )}
                            onClick={() => setSidebarOpen(false)}
                          >
                            {active && !isCompact && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[var(--adm-brand)]" />}
                            {/* Fluent Color icons carry their own colours, so they aren't tinted. */}
                            <Icon
                              icon={item.icon}
                              width={isCompact ? 24 : 20}
                              className={cn('flex-none transition-transform duration-150 group-hover:scale-110', active && 'scale-110')}
                            />
                            {!isCompact && <span className="flex-1 truncate">{item.title}</span>}
                            {item.key === 'notifications' && unreadCount > 0 && (
                              <span className={cn(
                                'flex items-center justify-center rounded-full bg-[var(--adm-brand)] font-bold text-white',
                                isCompact ? 'absolute -right-0.5 -top-0.5 h-4 min-w-4 px-1 text-[9px]' : 'ml-auto h-5 min-w-5 px-1.5 text-[10px]',
                              )}>
                                {unreadCount > 9 ? '9+' : unreadCount}
                              </span>
                            )}
                          </Link>
                        );
                        if (!isCompact) return linkEl;
                        return (
                          <Tooltip key={item.key}>
                            <TooltipTrigger asChild><div className="relative">{linkEl}</div></TooltipTrigger>
                            <TooltipContent side="right" sideOffset={12} className="text-xs font-medium">{item.title}</TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer: appearance + account */}
          <div className={cn('w-full flex-none border-t border-[var(--adm-side-border)] p-3', isCompact && 'flex flex-col items-center gap-1 px-0')}>
            {isCompact ? (
              <>
                <ThemeSwitch compact darkMode={darkMode} setTheme={setTheme} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={onToggle} className="flex h-10 w-10 items-center justify-center rounded-xl text-[var(--adm-side-muted)] hover:bg-[var(--adm-side-hover)] hover:text-[var(--adm-side-text)] transition-colors" aria-label="Expand sidebar">
                      <Icon icon="solar:round-alt-arrow-right-linear" width={22} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={12} className="text-xs">Expand</TooltipContent>
                </Tooltip>
              </>
            ) : (
              <>
                <p className="mb-1.5 px-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[var(--adm-side-muted)]">Appearance</p>
                <ThemeSwitch darkMode={darkMode} setTheme={setTheme} />
                <div className="mt-3 flex items-center gap-2.5 rounded-xl p-1.5">
                  <div className="relative flex-none">
                    {avatar('h-9 w-9')}
                    <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[var(--adm-side-bg)] bg-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-[var(--adm-text)]">{user?.full_name}</p>
                    <p className="truncate text-[11px] capitalize text-[var(--adm-side-muted)]">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button onClick={handleLogout} className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--adm-side-muted)] hover:bg-[var(--adm-side-hover)] hover:text-[var(--adm-brand)] transition-colors" aria-label="Log out" data-testid="admin-logout-btn">
                        <Icon icon="solar:logout-2-linear" width={18} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">Log out</TooltipContent>
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        </aside>

        {/* ══════════════════════════════════════════════════════════
            CONTENT AREA
            ══════════════════════════════════════════════════════════ */}
        <div className="flex w-full min-w-0 flex-1 flex-col overflow-hidden">

          {/* ── Top Header ────────────────────────────────────────── */}
          <header className="flex h-16 flex-none items-center gap-2 border-b border-[var(--adm-border)] bg-[var(--adm-surface)]/85 px-3 backdrop-blur-xl md:gap-3 md:px-6">
            <button
              className={cn('md:hidden', headerIconBtn)}
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Icon icon="solar:hamburger-menu-linear" width={22} />
            </button>

            {/* Title + breadcrumbs */}
            <div className="flex min-w-0 flex-col">
              <h2 className="truncate text-[15px] font-bold leading-tight text-[var(--adm-text)]">{pageTitle}</h2>
              <nav className="mt-0.5 hidden items-center gap-1 text-[11px] text-[var(--adm-muted)] sm:flex" aria-label="Breadcrumb">
                {breadcrumbs.map((crumb, i) => (
                  <React.Fragment key={crumb.path}>
                    {i > 0 && <Icon icon="solar:alt-arrow-right-linear" width={10} className="flex-none opacity-50" />}
                    {i < breadcrumbs.length - 1 ? (
                      <Link to={crumb.path} className="truncate hover:text-[var(--adm-text)] hover:underline">{crumb.label}</Link>
                    ) : (
                      <span className="truncate text-[var(--adm-subtle)]">{crumb.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            </div>

            <div className="flex-1" />

            {/* Review mode — the one switch, always in view */}
            <ReviewModePill canEdit={isAdmin} />

            {/* Theme switch */}
            <ThemeSwitch darkMode={darkMode} setTheme={setTheme} className="hidden lg:flex w-[168px]" />
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setTheme(!darkMode)}
                  className={cn('lg:hidden', headerIconBtn)}
                  data-testid="admin-theme-toggle"
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  <Icon icon={darkMode ? 'solar:sun-2-bold-duotone' : 'solar:moon-bold-duotone'} width={20} className={darkMode ? 'text-amber-400' : ''} />
                </button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">{darkMode ? 'Light mode' : 'Dark mode'}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/" className={cn('hidden sm:flex', headerIconBtn)} aria-label="View site">
                  <Icon icon="solar:global-linear" width={20} />
                </Link>
              </TooltipTrigger>
              <TooltipContent className="text-xs">View site</TooltipContent>
            </Tooltip>

            {/* Notifications */}
            <Popover>
              <PopoverTrigger asChild>
                <button className={cn('relative', headerIconBtn)} aria-label="Notifications">
                  <Icon icon="solar:bell-bold-duotone" width={21} />
                  {unreadCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--adm-brand)] px-1 text-[9px] font-bold text-white ring-2 ring-[var(--adm-surface)]">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={8} className="w-80 rounded-2xl border-[var(--adm-border)] bg-[var(--adm-surface)] p-0 shadow-2xl">
                <div className="flex items-center justify-between p-4 pb-3">
                  <h4 className="text-sm font-semibold text-[var(--adm-text)]">Notifications</h4>
                  {unreadCount > 0 && (
                    <span className="rounded-full bg-[var(--adm-brand-soft)] px-2.5 py-0.5 text-[10px] font-semibold text-[var(--adm-brand)]">{unreadCount} new</span>
                  )}
                </div>
                <div className="h-px bg-[var(--adm-border)]" />
                <div className="nn-scroll max-h-[300px] overflow-y-auto">
                  {notifications.length > 0 ? notifications.slice(0, 5).map((n, i) => (
                    <div key={n._id || i} className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-[var(--adm-hover)]">
                      <div className="mt-1.5 h-2 w-2 flex-none rounded-full bg-[var(--adm-brand)]" />
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-[13px] leading-snug text-[var(--adm-text)]">{n.message || n.title || 'Notification'}</p>
                        <p className="mt-1 text-[11px] text-[var(--adm-muted)]">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                  )) : (
                    <div className="flex flex-col items-center gap-2 py-10">
                      <Icon icon="solar:bell-off-line-duotone" width={32} className="text-[var(--adm-subtle)]" />
                      <p className="text-[13px] text-[var(--adm-muted)]">No new notifications</p>
                    </div>
                  )}
                </div>
                <div className="h-px bg-[var(--adm-border)]" />
                <div className="p-2">
                  <Link to="/admin/notifications">
                    <Button variant="ghost" size="sm" className="w-full rounded-xl text-xs text-[var(--adm-muted)]">View all notifications</Button>
                  </Link>
                </div>
              </PopoverContent>
            </Popover>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-0.5 flex items-center gap-2 rounded-xl px-1.5 py-1 outline-none transition-colors hover:bg-[var(--adm-hover)]" aria-label="Account menu">
                  <div className="relative">
                    {avatar('h-8 w-8')}
                    <span className="absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-2 border-[var(--adm-surface)] bg-emerald-500" />
                  </div>
                  <Icon icon="solar:alt-arrow-down-linear" width={14} className="hidden text-[var(--adm-muted)] md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="w-60 rounded-2xl border-[var(--adm-border)] bg-[var(--adm-surface)] p-1.5 shadow-2xl">
                <DropdownMenuLabel className="flex items-center gap-3 p-2 font-normal">
                  {avatar('h-10 w-10 flex-none')}
                  <div className="flex min-w-0 flex-col">
                    <p className="truncate text-[13px] font-semibold text-[var(--adm-text)]">{user?.full_name}</p>
                    <p className="truncate text-[11px] text-[var(--adm-muted)]">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-1 bg-[var(--adm-border)]" />
                {[
                  ['/admin/settings', 'solar:settings-bold-duotone', 'Settings'],
                  ['/admin/analytics', 'solar:chart-square-bold-duotone', 'Analytics'],
                  ['/admin/support', 'solar:help-bold-duotone', 'Help & feedback'],
                ].map(([href, icon, label]) => (
                  <DropdownMenuItem key={href} className="cursor-pointer gap-3 rounded-lg px-2 py-2 text-[13px] text-[var(--adm-text)]" onClick={() => navigate(href)}>
                    <Icon icon={icon} width={20} className="text-[var(--adm-muted)]" /> {label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="my-1 bg-[var(--adm-border)]" />
                <DropdownMenuItem className="cursor-pointer gap-3 rounded-lg px-2 py-2 text-[13px] text-[var(--adm-brand)] focus:text-[var(--adm-brand)]" onClick={handleLogout}>
                  <Icon icon="solar:logout-3-bold-duotone" width={20} /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          {/* ── Main Content ──────────────────────────────────────── */}
          <main className="nn-scroll flex-1 overflow-y-auto bg-[var(--adm-bg)]">
            <div className="mx-auto w-full max-w-[1600px] p-4 md:p-6 lg:p-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
